import React from 'react';
import { FaExclamationTriangle, FaSignInAlt } from 'react-icons/fa';
import keycloak from '../config/keycloak';

interface SessionTimeoutModalProps {
  isOpen: boolean;
  onLoginRedirect?: () => void;
}

const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({ 
  isOpen, 
  onLoginRedirect 
}) => {
  if (!isOpen) return null;

  const handleLoginRedirect = () => {
    console.log('🔐 Redirecting to login due to session timeout...');
    
    if (onLoginRedirect) {
      onLoginRedirect();
    } else {
      // Default: trigger Keycloak login
      try {
        keycloak.login({
          redirectUri: window.location.origin + '/dashboard'
        });
      } catch (error) {
        console.error('Failed to redirect to login:', error);
        // Fallback: redirect to home
        window.location.href = '/';
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[9999]">
      {/* Backdrop - prevents any interaction */}
      <div className="absolute inset-0" />

      {/* Modal */}
      <div className="relative bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-[#eaeaea]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center">
            <FaExclamationTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-[18px] font-semibold text-[#171717] text-center mb-3">
          Session Expired
        </h2>

        {/* Message */}
        <p className="text-[13px] text-[#888] text-center mb-6 leading-relaxed">
          Your session has timed out due to inactivity. For security reasons,
          you have been logged out and need to sign in again to continue.
        </p>

        {/* Security notice */}
        <div className="bg-[#fafafa] border border-[#eaeaea] rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <svg className="w-4 h-4 text-[#888] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-[12px] font-medium text-[#444]">Security Notice</p>
              <p className="text-[12px] text-[#888] mt-1">
                This automatic logout helps protect your data when you're away from your device.
              </p>
            </div>
          </div>
        </div>

        {/* Login button */}
        <button
          onClick={handleLoginRedirect}
          className="w-full bg-[#171717] text-white py-2.5 px-4 rounded-lg hover:bg-[#333] transition-colors duration-150 text-[13px] font-medium flex items-center justify-center space-x-2"
        >
          <FaSignInAlt className="w-3.5 h-3.5" />
          <span>Sign In Again</span>
        </button>

        {/* Footer */}
        <p className="text-[11px] text-[#aaa] text-center mt-4">
          You will be redirected to the login page
        </p>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;
